import * as React from "react";

import {
    QRCodeWrapper,
    Subtitle,
    GlideLogo,
    GlideSpan,
    Footer,
    BodyWrapper,
    FooterWrapper,
    AppInfoBodyWrapper,
    Wrapper,
} from "./share-page-style";
import copyToClipboard from "copy-to-clipboard";
import { getLocalizedString } from "common/components/localization";
import { trackEvent } from "../../../../analytics";
import { RenderEnvironment } from "common/components/types";
import AppBanner from "webapp/chrome/views/app-banner/app-banner";
import SimpleTableView, { SimpleTableRowDescription } from "webapp/ui/views/simple-table/simple-table-view";
import { SimpleTableRowStyle } from "common/generator/components/simple-table-types";
import { getIdealShareUrl } from "webapp/lib/share-utils";
import { glideViralReferralLink, isEmptyOrUndefined } from "common/Support";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { ErrorBoundary } from "lib/error-boundary";

const QRCode = lazilyLoadedComponent("qrcode.react", () => import("qrcode.react"));

interface Props {
    readonly title: string | undefined;
    readonly author: string | undefined;
    readonly displayQRCode: boolean;
    readonly renderEnvironment: RenderEnvironment;
    readonly onFeedback: (() => void) | undefined;
    readonly onReport: (() => void) | undefined;
}

interface State {}

function Glide() {
    return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28ZM18.1083 17.8297C17.5653 19.5664 16.242 20.7289 14.142 21.1119C11.1703 21.6539 8.34884 20.3261 7.55753 15.9874C6.8421 12.0647 8.00652 7.00083 13.5736 5.98549C16.7632 5.40375 19.0694 6.60031 19.9013 9.25306L17.4053 10.1586C16.8549 8.37573 15.5667 7.70998 13.7836 8.03518C10.2572 8.67834 9.71352 12.5441 10.2483 15.4762C10.8011 18.5073 12.2804 19.3225 14.3011 18.9539C17.154 18.4336 17.7976 16.1259 17.7932 14.7552L17.793 14.3049L13.9892 14.9986L13.9881 12.9722L20.1098 11.8557L20.1429 19.6694L18.3203 20.0019L18.1083 17.8297Z"
            />
        </svg>
    );
}

export default class SharePage extends React.PureComponent<Props, State> {
    public state: State = { currentPage: "info" };

    private canShowShare = (navigator as any).share !== undefined;

    private onLinkClick = async () => {
        const nav = navigator as any;
        const { appEnvironment } = this.props.renderEnvironment;
        const { appID } = appEnvironment;
        const url = getIdealShareUrl(appEnvironment);
        if (!this.canShowShare) {
            copyToClipboard(url);
            trackEvent("share", { app_id: appID, method: "copy link" });
        } else {
            try {
                await nav.share({ title: getLocalizedString("shareApp"), url: window.location.href });
                trackEvent("share", { app_id: appID, method: "share sheet" });
            } catch {
                // do nothing
            }
        }
    };

    private onFeedbackClick = () => {
        if (this.props.onFeedback === undefined) return;

        const { appID } = this.props.renderEnvironment.appEnvironment;
        trackEvent("feedback opened", { app_id: appID });
        this.props.onFeedback();
    };

    private onReportClick = () => {
        if (this.props.onReport === undefined) return;

        const { appID } = this.props.renderEnvironment.appEnvironment;
        trackEvent("report opened", { app_id: appID });
        this.props.onReport();
    };

    public render(): React.ReactNode {
        const { renderEnvironment, title, author, displayQRCode, onFeedback, onReport } = this.props;
        const { appEnvironment } = renderEnvironment;
        const { appIcon } = appEnvironment;
        const url = getIdealShareUrl(appEnvironment);
        const showBranding = !appEnvironment.eminenceFlags.removeBranding;

        let content: React.ReactNode;

        if (displayQRCode) {
            content = (
                <BodyWrapper>
                    <QRCodeWrapper>
                        <ErrorBoundary>
                            <React.Suspense fallback={<div />}>
                                <QRCode
                                    style={{ imageRendering: "pixelated" }}
                                    size={210}
                                    value={url}
                                    fgColor="#000"
                                    bgColor="#fff"
                                />
                            </React.Suspense>
                        </ErrorBoundary>
                    </QRCodeWrapper>
                    <Subtitle>{getLocalizedString("shareThisAppBy")}</Subtitle>
                </BodyWrapper>
            );
        } else {
            const rows: SimpleTableRowDescription[] = [];
            if (!isEmptyOrUndefined(author)) {
                rows.push({ label: getLocalizedString("author"), value: author, style: SimpleTableRowStyle.None });
            }
            if (!appEnvironment.eminenceFlags.canDisableShare || appEnvironment.appFeatures.disableSharing !== true) {
                rows.push({
                    label: getLocalizedString("share"),
                    value: url.replace("https://", ""),
                    style: SimpleTableRowStyle.Link,
                    action: this.onLinkClick,
                });
            }
            if (onFeedback !== undefined) {
                rows.push({
                    label: getLocalizedString("support"),
                    value: getLocalizedString("sendFeedback"),
                    style: SimpleTableRowStyle.PushScreen,
                    action: this.onFeedbackClick,
                });
            }
            if (onReport !== undefined) {
                rows.push({
                    label: getLocalizedString("security"),
                    value: getLocalizedString("reportThisApp"),
                    style: SimpleTableRowStyle.PushScreen,
                    action: this.onReportClick,
                });
            }

            content = (
                <AppInfoBodyWrapper>
                    <AppBanner title={title ?? ""} icon={appIcon} />
                    <SimpleTableView title={undefined} footer={undefined} rows={rows} />

                    {showBranding && (
                        <FooterWrapper href={glideViralReferralLink("about")} target="_blank">
                            <GlideLogo>
                                <Glide />
                            </GlideLogo>
                            <Footer>
                                {getLocalizedString("madeWithPrefix")}
                                <GlideSpan>glideapps.com</GlideSpan>
                                {getLocalizedString("madeWithPostfix")}
                            </Footer>
                        </FooterWrapper>
                    )}
                </AppInfoBodyWrapper>
            );
        }

        return <Wrapper>{content}</Wrapper>;
    }
}
